/**
 * Variant Picker Compatible Option Validation Script
 * 
 * This script extends the variant-picker functionality to handle
 * option validation and rebuilding based on variant combinations.
 * It's designed to work with the existing variant-picker.js component.
 */

class VariantPickerOptionValidator {
  constructor(variantPicker, options = {}) {
    this.variantPicker = variantPicker;
    this.productData = variantPicker.data;
    this.optionSelectors = variantPicker.optionSelectors;
    this.selectedOptions = [];
    
    // Configuration options
    this.hideUnavailableOptions = options.hideUnavailableOptions !== false; // Default true
    this.debugMode = options.debugMode || false;
    this.autoSelectInStock = options.autoSelectInStock !== false; // Default true
    this.autoSelectInStockOnChange = options.autoSelectInStockOnChange !== false; // Default true
    this.isAutoSelecting = false; // Flag to prevent validation interference
    
    // Initialize the validator
    this.init();
  }

  init() {
    // Add event listener for variant changes
    this.variantPicker.addEventListener('change', this.handleVariantChange.bind(this));
    
    // Initial rebuild
    this.rebuildOptions();
    
    // Auto-select an in-stock variant on load (with a small delay to ensure DOM is ready)
    if (this.autoSelectInStock) {
      setTimeout(() => {
        this.autoSelectInStockVariant();
      }, 200); // Increased delay to ensure media gallery is ready
    }
  }

  /**
   * Handles variant change events and rebuilds options
   */
  handleVariantChange() {
    // Check if we should skip validation to avoid interfering with normal variant-picker behavior
    if (this.shouldSkipValidation()) {
      return;
    }
    
    // Shorter delay to make validation more responsive while still allowing
    // variant-picker to complete its immediate updates (image, price, etc.)
    setTimeout(() => {
      this.rebuildOptions();
      
      // After rebuilding options, try to auto-select an in-stock variant
      this.autoSelectInStockAfterChange();
    }, 50);
  }

  /**
   * Determines if we should skip validation to avoid interfering with normal variant-picker behavior
   * @returns {boolean} - Whether to skip validation
   */
  shouldSkipValidation() {
    // Skip validation if we're in the middle of auto-selecting a variant
    if (this.isAutoSelecting) {
      return true;
    }
    
    // Don't skip validation for normal user interactions
    // Only skip if we're in the initial auto-selection phase
    return false;
  }

  /**
   * Validates if a combination of options is valid
   * @param {string} inputValue - The option value to validate
   * @param {number} optionLevel - The level of the option (0-based)
   * @returns {boolean} - Whether the combination is valid
   */
  validCombo(inputValue, optionLevel) {
    const variants = this.productData.product.variants;
    
    for (let i = 0; i < variants.length; i++) {
      const variant = variants[i];
      
      if (optionLevel === 1) {
        // Check if option1 matches selected and option2 matches input
        if (variant.options[0] === this.selectedOptions[0] && 
            variant.options[1] === inputValue) {
          return true;
        }
      } else if (optionLevel === 2) {
        // Check if option1, option2 match selected and option3 matches input
        if (variant.options[0] === this.selectedOptions[0] && 
            variant.options[1] === this.selectedOptions[1] && 
            variant.options[2] === inputValue) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Alternative validation method that checks if an option leads to any valid variant
   * @param {string} inputValue - The option value to validate
   * @param {number} optionLevel - The level of the option (0-based)
   * @returns {boolean} - Whether the combination is valid
   */
  validComboAlternative(inputValue, optionLevel) {
    const variants = this.productData.product.variants;
    
    for (let i = 0; i < variants.length; i++) {
      const variant = variants[i];
      
      // Check if this variant has the input value at the specified option level
      if (variant.options[optionLevel] === inputValue) {
        // Check if all previous options match the current selections
        let matches = true;
        for (let j = 0; j < optionLevel; j++) {
          if (this.selectedOptions[j] && variant.options[j] !== this.selectedOptions[j]) {
            matches = false;
            break;
          }
        }
        if (matches) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Gets the current selected options from the variant picker
   * @returns {Array} - Array of selected option values
   */
  getSelectedOptions() {
    const selectedOptions = [];
    
    this.optionSelectors.forEach((selector) => {
      if (selector.dataset.selectorType === 'dropdown') {
        // For dropdown selectors
        const selectedOption = selector.querySelector('.custom-select__btn');
        selectedOptions.push(selectedOption ? selectedOption.textContent.trim() : '');
      } else {
        // For radio button selectors
        const checkedInput = selector.querySelector('input:checked');
        selectedOptions.push(checkedInput ? checkedInput.value : '');
      }
    });
    
    return selectedOptions;
  }

  /**
   * Rebuilds options based on current selections
   */
  rebuildOptions() {
    this.selectedOptions = this.getSelectedOptions();
    
    if (this.debugMode) {
      console.log('Rebuilding options with selections:', this.selectedOptions);
    }
    
    // Always run validation for user interactions (not just when we have selections)
    // This ensures options repopulate correctly after each selection
    
    // Process each option selector (skip the first one as it's always available)
    for (let optionLevel = 1; optionLevel < this.optionSelectors.length; optionLevel++) {
      const selector = this.optionSelectors[optionLevel];
      const selectorType = selector.dataset.selectorType;
      
      if (selectorType === 'dropdown') {
        this.updateDropdownOptions(selector, optionLevel);
      } else {
        this.updateRadioOptions(selector, optionLevel);
      }
    }
    
    // Check if current selections are still valid and auto-select if needed
    this.validateCurrentSelections();
  }

  /**
   * Updates dropdown options based on validity
   * @param {Element} selector - The dropdown selector element
   * @param {number} optionLevel - The option level
   */
  updateDropdownOptions(selector, optionLevel) {
    const options = selector.querySelectorAll('.custom-select__option');
    
    options.forEach(option => {
      const optionValue = option.dataset.value;
      const isValid = this.validComboAlternative(optionValue, optionLevel);
      
      // Debug logging
      if (this.debugMode) {
        console.log(`Option ${optionValue} at level ${optionLevel}:`, isValid, 'Selected:', this.selectedOptions);
      }
      
      // Update option availability
      option.classList.toggle('is-unavailable', !isValid);
      option.classList.toggle('is-nonexistent', !isValid);
      
      // Hide/show option only if configured to do so
      if (this.hideUnavailableOptions) {
        option.hidden = !isValid;
      }
      
      // Update em element if it exists
      const em = option.querySelector('em');
      if (em) {
        em.hidden = isValid;
        if (!isValid) {
          em.textContent = isValid ? theme.strings.noStock : theme.strings.noVariant;
        }
      }
    });
  }

  /**
   * Updates radio button options based on validity
   * @param {Element} selector - The radio selector element
   * @param {number} optionLevel - The option level
   */
  updateRadioOptions(selector, optionLevel) {
    const inputs = selector.querySelectorAll('input[type="radio"]');
    
    inputs.forEach(input => {
      const isValid = this.validComboAlternative(input.value, optionLevel);
      
      // Debug logging
      if (this.debugMode) {
        console.log(`Radio ${input.value} at level ${optionLevel}:`, isValid, 'Selected:', this.selectedOptions);
      }
      
      // Update input availability
      input.disabled = !isValid;
      
      // Find associated label
      const label = selector.querySelector(`label[for="${input.id}"]`);
      if (label) {
        // Hide/show label based on validity only if configured to do so
        if (this.hideUnavailableOptions) {
          label.style.display = isValid ? '' : 'none';
        } else {
          // Just style it differently instead of hiding
          label.style.opacity = isValid ? 1 : 0.5;
          label.style.pointerEvents = isValid ? 'auto' : 'none';
        }
        
        // Alternative styling options (uncomment to use):
        // label.style.borderStyle = isValid ? 'solid' : 'dashed';
        // label.style.textDecoration = isValid ? '' : 'line-through';
      }
    });
  }

  /**
   * Auto-selects an in-stock variant after a user changes an option
   */
  autoSelectInStockAfterChange() {
    // Check if auto-selection on change is enabled
    if (!this.autoSelectInStockOnChange) {
      return;
    }
    
    const currentVariant = this.variantPicker.getSelectedVariant();
    
    // If current variant is already in stock, don't change it
    if (currentVariant && currentVariant.available) {
      if (this.debugMode) {
        console.log('Current variant is already in stock:', currentVariant);
      }
      return;
    }
    
    // Find an in-stock variant that matches the current selections
    const variants = this.productData.product.variants;
    const currentSelections = this.getSelectedOptions();
    
    // Find the first available variant that matches current selections
    const availableVariant = variants.find(variant => {
      if (!variant.available) return false;
      
      // Check if this variant matches the current selections
      return variant.options.every((option, index) => {
        return !currentSelections[index] || option === currentSelections[index];
      });
    });
    
    if (availableVariant) {
      if (this.debugMode) {
        console.log('Auto-selecting in-stock variant after change:', availableVariant);
      }
      
      // Set flag to prevent validation interference
      this.isAutoSelecting = true;
      
      // Select the remaining options for this variant with improved logic
      this.selectRemainingOptionsForVariantImproved(availableVariant);
      
      // Clear the auto-selecting flag after a delay
      setTimeout(() => {
        this.isAutoSelecting = false;
      }, 100);
    } else if (this.debugMode) {
      console.log('No in-stock variant found matching current selections');
    }
  }

  /**
   * Selects the remaining options for a variant (only the ones that aren't already selected)
   * @param {Object} variant - The variant to select options for
   */
  selectRemainingOptionsForVariant(variant) {
    const currentSelections = this.getSelectedOptions();
    
    if (this.debugMode) {
      console.log('Selecting remaining options for variant:', variant);
      console.log('Current selections:', currentSelections);
      console.log('Variant options:', variant.options);
    }
    
    variant.options.forEach((optionValue, index) => {
      // Only select options that aren't already selected
      if (index < this.optionSelectors.length && currentSelections[index] !== optionValue) {
        if (this.debugMode) {
          console.log(`Selecting option ${index}: ${optionValue} (was: ${currentSelections[index]})`);
        }
        
        const selector = this.optionSelectors[index];
        const selectorType = selector.dataset.selectorType;
        
        if (selectorType === 'dropdown') {
          this.selectDropdownOption(selector, optionValue);
        } else {
          this.selectRadioOption(selector, optionValue);
        }
      } else if (this.debugMode) {
        console.log(`Skipping option ${index}: ${optionValue} (already selected: ${currentSelections[index]})`);
      }
    });
    
    // Update the variant picker's internal state
    this.variantPicker.variant = variant;
    
    // Trigger all the variant-picker update methods manually
    this.variantPicker.updateMedia();
    this.variantPicker.updatePrice();
    this.variantPicker.updateAvailability();
    this.variantPicker.updateAddToCartButton();
    this.variantPicker.updateVariantInput();
    this.variantPicker.updateSku();
    this.variantPicker.updateWeight();
    this.variantPicker.updateBarcode();
    this.variantPicker.updateBackorderText();
    this.variantPicker.updatePickupAvailability();
    this.variantPicker.updateInventoryStatus(variant);
    
    // Ensure media gallery updates properly
    this.ensureMediaUpdate(variant);
    
    this.dispatchVariantChange(variant);
  }

  /**
   * Dispatches on:variant:change on both variant-picker and document so fulfillment boxes receive it.
   * Also dispatches variant-picker:auto-select-complete for listeners that need to know auto-select finished.
   */
  dispatchVariantChange(variant) {
    const form = this.variantPicker.productForm || (this.variantPicker.section && this.variantPicker.section.querySelector('.js-product-form-main'));
    const detail = {
      form: form,
      variant: variant,
      product: this.productData.product
    };
    this.variantPicker.dispatchEvent(new CustomEvent('on:variant:change', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('on:variant:change', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('variant-picker:auto-select-complete', { bubbles: true, detail }));
  }

  /**
   * Improved method to select remaining options for a variant
   * @param {Object} variant - The variant to select options for
   */
  selectRemainingOptionsForVariantImproved(variant) {
    const currentSelections = this.getSelectedOptions();
    
    if (this.debugMode) {
      console.log('=== IMPROVED SELECTION ===');
      console.log('Selecting remaining options for variant:', variant);
      console.log('Current selections:', currentSelections);
      console.log('Variant options:', variant.options);
    }
    
    // Process each option with a small delay to ensure proper selection
    variant.options.forEach((optionValue, index) => {
      if (index < this.optionSelectors.length && currentSelections[index] !== optionValue) {
        setTimeout(() => {
          if (this.debugMode) {
            console.log(`[DELAYED] Selecting option ${index}: ${optionValue} (was: ${currentSelections[index]})`);
          }
          
          const selector = this.optionSelectors[index];
          const selectorType = selector.dataset.selectorType;
          
          if (selectorType === 'dropdown') {
            this.selectDropdownOptionImproved(selector, optionValue);
          } else {
            this.selectRadioOptionImproved(selector, optionValue);
          }
        }, index * 50); // 50ms delay between each selection
      } else if (this.debugMode) {
        console.log(`Skipping option ${index}: ${optionValue} (already selected: ${currentSelections[index]})`);
      }
    });
    
    // Update variant picker state after all selections
    setTimeout(() => {
      this.variantPicker.variant = variant;
      
      // Trigger all the variant-picker update methods manually
      this.variantPicker.updateMedia();
      this.variantPicker.updatePrice();
      this.variantPicker.updateAvailability();
      this.variantPicker.updateAddToCartButton();
      this.variantPicker.updateVariantInput();
      this.variantPicker.updateSku();
      this.variantPicker.updateWeight();
      this.variantPicker.updateBarcode();
      this.variantPicker.updateBackorderText();
      this.variantPicker.updatePickupAvailability();
      this.variantPicker.updateInventoryStatus(variant);
      
      // Ensure media gallery updates properly
      this.ensureMediaUpdate(variant);
      
    // Dispatch on:variant:change so fulfillment boxes and other listeners update immediately
    this.dispatchVariantChange(variant);
      
      if (this.debugMode) {
        console.log('=== SELECTION COMPLETE ===');
      }
    }, variant.options.length * 50 + 100);
  }

  /**
   * Improved dropdown option selection - uses customSelect.selectOption when available for proper init
   */
  selectDropdownOptionImproved(selector, optionValue) {
    if (this.debugMode) {
      console.log(`[IMPROVED] Attempting to select dropdown option: ${optionValue}`);
    }
    
    const option = selector.querySelector(`[data-value="${optionValue}"]`);
    if (option) {
      const customSelect = selector.querySelector('custom-select');
      if (customSelect && typeof customSelect.selectOption === 'function') {
        customSelect.selectOption(option);
      } else {
        const button = selector.querySelector('.custom-select__btn');
        if (button) button.textContent = (option.firstElementChild || option).textContent.trim();
        option.click();
      }
      if (this.debugMode) {
        console.log(`[IMPROVED] Successfully selected dropdown option: ${optionValue}`);
      }
    } else {
      if (this.debugMode) {
        console.log(`[IMPROVED] Could not find dropdown option with value: ${optionValue}`);
      }
    }
  }

  /**
   * Improved radio option selection
   */
  selectRadioOptionImproved(selector, optionValue) {
    if (this.debugMode) {
      console.log(`[IMPROVED] Attempting to select radio option: ${optionValue}`);
    }
    
    const radio = selector.querySelector(`input[value="${optionValue}"]`);
    if (radio) {
      // Method 1: Direct check
      radio.checked = true;
      
      // Method 2: Trigger events
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      radio.dispatchEvent(new Event('click', { bubbles: true }));
      
      if (this.debugMode) {
        console.log(`[IMPROVED] Successfully selected radio option: ${optionValue}`);
      }
    } else {
      if (this.debugMode) {
        console.log(`[IMPROVED] Could not find radio option with value: ${optionValue}`);
      }
    }
  }

  /**
   * Auto-selects an in-stock variant when the page loads
   */
  autoSelectInStockVariant() {
    const variants = this.productData.product.variants;
    
    // Check if there's already a selected variant
    const currentVariant = this.variantPicker.getSelectedVariant();
    if (currentVariant && currentVariant.available) {
      if (this.debugMode) {
        console.log('In-stock variant already selected:', currentVariant);
      }
      return;
    }
    
    // Find the first available variant
    const availableVariant = variants.find(variant => variant.available);
    
    if (!availableVariant) {
      if (this.debugMode) {
        console.log('No in-stock variants found, selecting first variant');
      }
      // If no in-stock variants, select the first variant anyway
      if (variants.length > 0) {
        this.selectVariantOptions(variants[0]);
      }
      return;
    }
    
    if (this.debugMode) {
      console.log('Auto-selecting in-stock variant:', availableVariant);
    }
    
    // Select the options for this variant
    this.selectVariantOptions(availableVariant);
  }

  /**
   * Selects the options for a specific variant
   * @param {Object} variant - The variant to select
   */
  selectVariantOptions(variant) {
    // Set flag to prevent validation interference
    this.isAutoSelecting = true;
    
    variant.options.forEach((optionValue, index) => {
      if (index < this.optionSelectors.length) {
        const selector = this.optionSelectors[index];
        const selectorType = selector.dataset.selectorType;
        
        if (selectorType === 'dropdown') {
          this.selectDropdownOption(selector, optionValue);
        } else {
          this.selectRadioOption(selector, optionValue);
        }
      }
    });
    
    // Update the variant picker's internal state
    this.variantPicker.variant = variant;
    
    // Trigger all the variant-picker update methods manually
    this.variantPicker.updateMedia();
    this.variantPicker.updatePrice();
    this.variantPicker.updateAvailability();
    this.variantPicker.updateAddToCartButton();
    this.variantPicker.updateVariantInput();
    this.variantPicker.updateSku();
    this.variantPicker.updateWeight();
    this.variantPicker.updateBarcode();
    this.variantPicker.updateBackorderText();
    this.variantPicker.updatePickupAvailability();
    this.variantPicker.updateInventoryStatus(variant);
    
    // Ensure media gallery updates properly
    this.ensureMediaUpdate(variant);
    
    this.dispatchVariantChange(variant);
    
    // Clear the auto-selecting flag after a delay
    setTimeout(() => {
      this.isAutoSelecting = false;
    }, 200);
  }

  /**
   * Ensures the media gallery updates properly for the selected variant
   * @param {Object} variant - The selected variant
   */
  ensureMediaUpdate(variant) {
    if (!variant.featured_media) return;
    
    // Get the media gallery element
    const mediaGallery = this.variantPicker.section.querySelector('media-gallery');
    if (mediaGallery) {
      // Find the variant media element
      const variantMedia = mediaGallery.querySelector(
        `[data-media-id="${variant.featured_media.id}"]`
      );
      
      if (variantMedia) {
        // Set the active media
        mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }
    
    // Also try the variant-picker's own media update method
    if (this.variantPicker.updateMedia) {
      this.variantPicker.updateMedia();
    }
  }

  /**
   * Selects an option in a dropdown selector - uses customSelect.selectOption when available
   */
  selectDropdownOption(selector, optionValue) {
    if (this.debugMode) {
      console.log(`Attempting to select dropdown option: ${optionValue}`);
    }
    
    const option = selector.querySelector(`[data-value="${optionValue}"]`);
    if (option) {
      const customSelect = selector.querySelector('custom-select');
      if (customSelect && typeof customSelect.selectOption === 'function') {
        customSelect.selectOption(option);
      } else {
        const button = selector.querySelector('.custom-select__btn');
        if (button) button.textContent = (option.firstElementChild || option).textContent.trim();
        option.click();
        selector.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.debugMode) {
        console.log(`Successfully selected dropdown option: ${optionValue}`);
      }
    } else {
      if (this.debugMode) {
        console.log(`Could not find dropdown option with value: ${optionValue}`);
      }
    }
  }

  /**
   * Selects an option in a radio button selector
   * @param {Element} selector - The radio selector
   * @param {string} optionValue - The value to select
   */
  selectRadioOption(selector, optionValue) {
    if (this.debugMode) {
      console.log(`Attempting to select radio option: ${optionValue}`);
    }
    
    const radio = selector.querySelector(`input[value="${optionValue}"]`);
    if (radio) {
      if (this.debugMode) {
        console.log(`Found radio option:`, radio);
      }
      
      radio.checked = true;
      
      // Trigger change event on the radio button
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      
      if (this.debugMode) {
        console.log(`Successfully selected radio option: ${optionValue}`);
      }
    } else {
      if (this.debugMode) {
        console.log(`Could not find radio option with value: ${optionValue}`);
      }
    }
  }

  /**
   * Validates current selections and auto-selects valid options if needed
   */
  validateCurrentSelections() {
    let hasChanges = false;
    let changedViaRadio = false;
    
    for (let optionLevel = 1; optionLevel < this.optionSelectors.length && !hasChanges; optionLevel++) {
      const selector = this.optionSelectors[optionLevel];
      const selectorType = selector.dataset.selectorType;
      
      if (selectorType === 'dropdown') {
        const button = selector.querySelector('.custom-select__btn');
        const currentValue = button ? (button.firstElementChild ? button.firstElementChild.textContent : button.textContent).trim() : '';
        const options = selector.querySelectorAll('.custom-select__option');
        let currentOption = null;
        let firstValidOption = null;
        for (let i = 0; i < options.length; i++) {
          const opt = options[i];
          const optValue = opt.dataset.value || (opt.firstElementChild ? opt.firstElementChild.textContent : opt.textContent).trim();
          if (optValue === currentValue) currentOption = opt;
          if (!firstValidOption && !opt.classList.contains('is-unavailable')) firstValidOption = opt;
        }
        if (currentOption && currentOption.classList.contains('is-unavailable') && firstValidOption) {
          const customSelect = selector.querySelector('custom-select');
          if (customSelect && typeof customSelect.selectOption === 'function') {
            customSelect.selectOption(firstValidOption);
          } else {
            firstValidOption.click();
          }
          hasChanges = true;
        }
      } else {
        const checkedInput = selector.querySelector('input:checked');
        const validInput = selector.querySelector('input:not(:disabled)');
        
        if (checkedInput && checkedInput.disabled && validInput) {
          validInput.checked = true;
          hasChanges = true;
          changedViaRadio = true;
        }
      }
    }
    
    if (hasChanges && changedViaRadio) {
      this.variantPicker.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

/**
 * Initialize the option validator when variant-picker is ready
 */
document.addEventListener('DOMContentLoaded', function() {
  // Wait for variant-picker to be initialized
  const initValidator = () => {
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker && variantPicker.data) {
      // Initialize with options to NOT hide unavailable options by default
      new VariantPickerOptionValidator(variantPicker, {
        hideUnavailableOptions: true,
        debugMode: false,
        autoSelectInStock: true,
        autoSelectInStockOnChange: true
      });
    } else {
      // Retry after a short delay
      setTimeout(initValidator, 100);
    }
  };
  
  initValidator();
});

/**
 * Alternative initialization method for immediate use
 * Use this if you want to initialize manually
 */
function initializeVariantPickerValidator(options = {}) {
  const variantPicker = document.querySelector('variant-picker');
  if (variantPicker && variantPicker.data) {
    return new VariantPickerOptionValidator(variantPicker, options);
  }
  return null;
}

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { VariantPickerOptionValidator, initializeVariantPickerValidator };
}